import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { Equipment } from 'src/app/shared/models/dtos/register-form/equipment/equipment';
import { AddRemoveEquipmentRequestDTO } from 'src/app/shared/models/dtos/requests/add-remove-equipments-request-dto/add-remove-equipments-request-dto';
import { EquipmentSAPRegistrationRequestDTO } from 'src/app/shared/models/dtos/requests/equipment-sap-registrarion-request-dto/equipment-sap-registration-request-dto';
import { ProductRequestDTO } from 'src/app/shared/models/dtos/requests/product-request-dto/product-request-dto';
import { ProductDTO } from 'src/app/shared/models/dtos/responses/product-dto/product-dto';
import { SAPProductResponseDTO } from 'src/app/shared/models/dtos/responses/sap-product-response-dto/sap-product-reponse-dto';
import { SerialModelValidationResponseDTO } from 'src/app/shared/models/dtos/responses/serial-model-validation-response-dto/serial-model-validation-response-dto';
import { FamilyDTO } from 'src/app/shared/models/dtos/responses/family-dto/family-dto';
import { FamilyRequestDTO } from 'src/app/shared/models/dtos/requests/family-request-dto/family-request-dto';
import { FamilyDetailedDTO } from 'src/app/shared/models/dtos/responses/family-detailed-dto/family-detailed-dto';

@Injectable({ providedIn: 'root' })
/**
 * Service to interact with LnR's Product Service
 */
export class ProductService {
  private backendURL = '/prd/api';
  private backendAuthURL = '/prd/api/a';

  /**
   * Injects HttpClient to interact with LnR Backend
   * @constructor
   */
  constructor(private httpClient: HttpClient) {}

  /**
   * Builds URL from endpoint with authentication
   * @param {string} endpoint
   * @return {string}
   */
  private buildAuthURL(endpoint: string): string {
    return `${this.backendAuthURL}/${endpoint}`;
  }

  /**
   * Get a printer by passing serial number
   * @param {string} serial represents a printer serial code
   * @return {Promise<string>}
   */
  getPrinter(serial: string): Observable<any> {
    const endpoint = `equipment/model/${serial}`;
    return this.httpClient
      .get(this.buildURL(endpoint))
      .pipe(map((result) => result));
  }

  /**
   * Gets the equipments of a business.
   * @param {number} businessId
   * @return {Observable<Equipment[]>}
   */
  getBusinessEquipments(businessId: number): Observable<Equipment[]> {
    const endpoint = `equipment/list-from-business/${businessId}`;
    return this.httpClient.get<Equipment[]>(this.buildAuthURL(endpoint));
  }

  /**
   * Gets the equipments of a business.
   * @param {number} businessId
   * @param {number} page
   * @param {number} pageSize
   * @return {Observable<Equipment[]>}
   */
  getBusinessEquipmentsPaginated(
    businessId: number,
    page: number,
    pageSize: number
  ): Observable<Equipment[]> {
    const endpoint = `equipment/list-from-business/${businessId}/paged`;
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    return this.httpClient.get<Equipment[]>(this.buildAuthURL(endpoint), {
      params,
    });
  }

  /**
   * Return the list of products
   * @param {number} page
   * @param {number} pageSize
   * @param {number} familyId
   * @return {Observable<ProductDTO[]>}
   */
  getProductList(
    page: number,
    pageSize: number,
    familyId?: string
  ): Observable<ProductDTO[]> {
    const endpoint = 'product';
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (familyId !== undefined) {
      params = params.set('familyId', familyId);
    }

    return this.httpClient.get<ProductDTO[]>(this.buildAuthURL(endpoint), {
      params,
    });
  }

  /**
   * Return the list of products
   * @param {number} id
   * @return {Observable<ProductDTO>}
   */
  getProductById(id: number): Observable<ProductDTO> {
    const endpoint = `product/${id}`;
    return this.httpClient.get<ProductDTO>(this.buildAuthURL(endpoint));
  }

  /**
   * Adds or deletes a list of equipments from a business
   * @param {AddRemoveEquipmentRequestDTO} list
   * @param {number} businessId
   * @return {any}
   */
  updateEquipmentList(
    list: AddRemoveEquipmentRequestDTO,
    businessId: number
  ): Observable<any> {
    const endpoint = `equipment/${businessId}`;

    return this.httpClient.post<any>(this.buildAuthURL(endpoint), list);
  }

  /**
   * @param {number} equipmentId
   * @param {string} note
   * @return {Observable}
   */
  putNoteEquipment(equipmentId: number, note: string): Observable<any> {
    const endpoint = `equipment/note/${equipmentId}`;
    return this.httpClient.post<any>(this.buildAuthURL(endpoint), {
      note: note,
    });
  }

  /**
   * Get URL containing a spreadsheet with warranties
   * @return {any}
   */
  getWarrantiesURL(): Observable<any> {
    const endpoint = 'warranty-info/url';

    return this.httpClient.get<any>(this.buildAuthURL(endpoint));
  }

  /**
   * @param {string[]} serials
   * @param {string} countryInitials
   * @return {Observable<SerialModelValidationResponseDTO[]>}
   */
  validateSerial(
    serials: string[],
    countryInitials: string
  ): Observable<SerialModelValidationResponseDTO[]> {
    const endpoint = 'equipment/serial/validate';
    return this.httpClient.post<SerialModelValidationResponseDTO[]>(
      this.buildURL(endpoint),
      {
        serialNumbers: serials,
        country: countryInitials,
      }
    );
  }

  /**
   * Get equipment family by serial
   * @param {string} serial
   * @return {Observable<FamilyDTO>}
   */
  getEquipmentFamilyBySerial(serial: string): Observable<FamilyDTO> {
    const endpoint = `equipment/family/serial/${serial}`;
    return this.httpClient.get<FamilyDTO>(this.buildURL(endpoint));
  }

  /**
   * Registers an equipment on Epson's SAP
   * @param {EquipmentSAPRegistrationRequestDTO} equipment
   * @return {Observable<SAPProductResponseDTO>}
   */
  epsonSAPEquipmentRegistration(
    equipment: EquipmentSAPRegistrationRequestDTO
  ): Observable<SAPProductResponseDTO> {
    const endpoint = 'equipment/sap-registration';
    return this.httpClient.post<SAPProductResponseDTO>(
      this.buildURL(endpoint),
      equipment
    );
  }

  /**
   * Gets a product service API version
   * @return {Observable<string>}
   */
  getApiVersion(): Observable<string> {
    const headers = new HttpHeaders().set(
      'Content-Type',
      'text/plain; charset=utf-8'
    );
    return this.httpClient.get(`${this.backendURL}/version`, {
      headers,
      responseType: 'text',
    });
  }

  /**
   * Builds URL from endpoint
   * @param {string} endpoint
   * @return {string}
   */
  private buildURL(endpoint: string): string {
    return `${this.backendURL}/${endpoint}`;
  }

  /**
   * Create new Product
   * @param {ProductRequestDTO} content
   * @return {Observable<ProductDTO>}
   */
  createProduct(content: ProductRequestDTO): Observable<ProductRequestDTO> {
    const endpoint = 'product';
    return this.httpClient.post<any>(this.buildAuthURL(endpoint), content);
  }

  /**
   * Create new Product
   * @param {ProductRequestDTO} content
   * @param {number} id
   * @return {Observable<ProductDTO>}
   */
  updateProduct(
    content: ProductRequestDTO,
    id: number
  ): Observable<ProductRequestDTO> {
    const endpoint = `product/${id}`;
    return this.httpClient.put<any>(this.buildAuthURL(endpoint), content);
  }

  /**
   * Edit Product
   * @param {ProductDTO} productId
   * @param {ProductDTO} newName
   * @param {ProductDTO} newImage
   * @return {Observable<ProductRequestDTO>}
   */
  editProduct(
    productId: number,
    newName?: string,
    newImage?: string
  ): Observable<ProductRequestDTO> {
    const endpoint = `product/${productId}`;
    return this.httpClient.put<any>(this.buildAuthURL(endpoint), {
      newName,
      newImage,
    });
  }

  /**
   * Validate Product by name
   * @param {string} name
   * @return {Observable<any>}
   */
  validateProductByName(name: string): Observable<any> {
    const endpoint = 'product/name/validate';
    const params: any = { name } as any;

    return this.httpClient.get<any>(this.buildAuthURL(endpoint), { params });
  }

  /**
   * Validate Product by header
   * @param {string[]} headers
   * @return {Observable<any>}
   */
  validateProductByHeader(headers: string[]): Observable<any> {
    const endpoint = 'header/validate';

    let params = new HttpParams();
    headers.forEach((header) => {
      params = params.append('headers', header);
    });

    return this.httpClient.get<any>(this.buildAuthURL(endpoint), { params });
  }

  /**
   * Return the list of products
   * @return {Observable<ProductDTO[]>}
   */
  getAllActiveProducts(): Observable<ProductDTO[]> {
    const endpoint = 'product/active';
    return this.httpClient.get<ProductDTO[]>(this.buildAuthURL(endpoint));
  }

  /**
   * Create a new product family
   * @param {FamilyRequestDTO} content
   * @return {Observable<FamilyDTO>}
   */
  createFamilyProduct(content: FamilyRequestDTO): Observable<FamilyDTO> {
    const endpoint = 'family';
    return this.httpClient.post<FamilyDTO>(
      this.buildAuthURL(endpoint),
      content
    );
  }

  /**
   * Edit an existent product family
   * @param {number} id
   * @param {FamilyRequestDTO} content
   * @return {Observable<FamilyDTO>}
   */
  editFamilyProduct(
    id: number,
    content: FamilyRequestDTO
  ): Observable<FamilyDTO> {
    const endpoint = `family/${id}`;
    return this.httpClient.put<FamilyDTO>(this.buildAuthURL(endpoint), content);
  }

  /**
   * Return the list of family
   * @return {Observable<FamilyDTO[]>}
   */
  getAllFamilyProduct(): Observable<FamilyDTO[]> {
    const endpoint = 'family';
    return this.httpClient.get<FamilyDTO[]>(this.buildAuthURL(endpoint));
  }

  /**
   * Return the list of detailed families
   * @return {Observable<FamilyDetailedDTO[]>}
   */
  getAllDetailedFamilyProduct(): Observable<FamilyDetailedDTO[]> {
    const endpoint = 'family/detailed';
    return this.httpClient.get<FamilyDetailedDTO[]>(
      this.buildAuthURL(endpoint)
    );
  }
}
